import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import {
  InfiniteScrollCustomEvent,
  IonButton,
  IonInfiniteScroll,
  IonInfiniteScrollContent,
} from '@ionic/angular/standalone';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  EmptyResultComponent,
  InfiniteScrollHeaderComponent,
  SkeletonCardTableComponent,
  SpinnerLoadingComponent,
  type IInfiniteScrollHeaderConfig,
  type IInfiniteScrollHeaderFilter,
} from '@okcargo-apps/layout-ionic';
import { DecaDocumentCategory } from '@okcargo-apps/shared-domain';
import { AccountService } from '../../account';
import { DecaListItemComponent } from '../deca-list-item/deca-list-item.component';
import { DecaStoreService } from '../deca-store.service';
import { IDecaListPeriod } from '../deca-store.model';

@Component({
  selector: 'lib-deca-list',
  templateUrl: './deca-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DecaListItemComponent,
    InfiniteScrollHeaderComponent,
    IonInfiniteScroll,
    IonInfiniteScrollContent,
    IonButton,
    TranslateModule,
    SkeletonCardTableComponent,
    SpinnerLoadingComponent,
    EmptyResultComponent,
  ],
})
export class DecaListComponent {
  private readonly decaStoreService = inject(DecaStoreService);
  private readonly accountService = inject(AccountService);
  private readonly translateService = inject(TranslateService);

  protected readonly userCompanyId = computed(() => this.accountService.account().company.id);

  public readonly category = input.required<DecaDocumentCategory>();
  public readonly detailRoute = input.required<string>();

  protected readonly vm = computed(() => {
    switch (this.category()) {
      case 'UPCOMING_DOCUMENTS_AS_SHIPPER':
        return this.decaStoreService.upcomingDocumentsAsShipper();
      case 'UPCOMING_DOCUMENTS_AS_CARRIER':
        return this.decaStoreService.upcomingDocumentsAsCarrier();
      case 'PAST_DOCUMENTS_AS_SHIPPER':
        return this.decaStoreService.pastDocumentsAsShipper();
      case 'PAST_DOCUMENTS_AS_CARRIER':
        return this.decaStoreService.pastDocumentsAsCarrier();
      case 'TODAY_DOCUMENTS_AS_DRIVER':
        return this.decaStoreService.todayDocumentsAsDriver();
    }
  });

  private readonly period = computed<IDecaListPeriod>(() => {
    const category = this.category();
    if (category.startsWith('UPCOMING')) return 'upcoming';
    if (category.startsWith('PAST')) return 'past';
    return 'today';
  });

  private readonly appliedFiltersChips = computed<IInfiniteScrollHeaderFilter[]>(() => {
    const filters = this.decaStoreService.activeFiltersFor(this.period());
    if (!filters) return [];

    const label = filters.withoutPlate
      ? this.translateService.instant('deca.search.without_plate')
      : filters.plate
        ? `${this.translateService.instant('deca.search.plate')}: ${filters.plate}`
        : undefined;

    if (!label) return [];

    return [
      {
        label,
        clear: () => this.decaStoreService.clearFilters(this.period()),
      },
    ];
  });

  protected readonly headerConfig = computed<IInfiniteScrollHeaderConfig>(() => {
    const currentVm = this.vm();

    return {
      text: this.translateService.instant('deca.list.item_count', { count: currentVm.totalElements }),
      lastUpdated: currentVm.lastLoadedAt,
      staleMessage: currentVm.isStale ? this.translateService.instant('general.stale_data') : undefined,
      refreshButton: currentVm.isStale
        ? {
            label: this.translateService.instant('general.refresh'),
            action: () => this.onRefreshUser(),
          }
        : undefined,
      appliedFilters: this.appliedFiltersChips(),
    };
  });

  protected onIonInfinite(event: InfiniteScrollCustomEvent): void {
    this.decaStoreService.loadNextPageFor(this.category());
    event.target.complete();
  }

  protected onRetry(): void {
    this.decaStoreService.retryFor(this.category());
  }

  protected onRefreshUser(): void {
    this.decaStoreService.refreshCollectionAsUser(this.category());
  }
}
